/**
 * component-collection-filters.js -- Collection Filters Web Component for Mirai Online
 *
 * CollectionFilters custom element:
 *  - Manages desktop filter dropdowns (open/close, outside click, Escape key)
 *  - Handles filter checkbox changes with AJAX grid updates via Section Rendering API
 *  - Handles price range filter with min/max number inputs
 *  - Manages sort dropdown (desktop) and sort drawer (mobile)
 *  - Opens/closes mobile filter drawer (bottom sheet) with focus trap and inert background
 *  - Updates browser URL via history.pushState for bookmarkable/shareable filter state
 *  - Listens for popstate to restore filter state on browser back/forward
 *  - Replaces #ProductGrid, #ActiveFilters, and #ProductCount via DOMParser after AJAX
 *  - Updates mobile drawer live result count via debounced background fetch
 *  - Dispatches 'filters:update' CustomEvent on document after grid replacement
 *  - Theme editor compatible via shopify:section:load listener
 *
 * Security notes (per threat model T-02-06 through T-02-10):
 *  - Section Rendering HTML comes from Shopify's server (Liquid-rendered) -- safe to inject via innerHTML
 *  - No client-side price logic; all prices from server response
 *  - Filter URLs from data-url-add/data-url-remove are server-generated (Shopify's filter_value properties)
 *    -- not constructed from user input
 *  - Debounce on filter changes prevents request flooding (300ms minimum)
 *  - Never constructs HTML strings from user-provided data
 */

/* ============================================================
   COLLECTION FILTERS WEB COMPONENT
   ============================================================ */

class CollectionFilters extends HTMLElement {
  constructor() {
    super();

    // Stable bound references for addEventListener/removeEventListener symmetry
    this._boundKeydown = this.handleKeydown.bind(this);
    this._boundPopstate = this.handlePopstate.bind(this);
    this._boundOutsideClick = this.handleOutsideClick.bind(this);

    // State
    this._activeDropdown = null;
    this._mobileDrawerOpen = false;
    this._sortDrawerOpen = false;
    this._releaseFocusTrap = null;
    this._releaseSortFocusTrap = null;
    this._triggerElement = null;
    this._debounceTimer = null;
    this._isLoading = false;
  }

  connectedCallback() {
    // Event delegation: filter checkbox changes
    this.addEventListener('change', this.onFilterChange.bind(this));

    // Event delegation: all click interactions
    this.addEventListener('click', this.onClickDelegate.bind(this));

    // Global keydown for Escape to close dropdown/drawer
    document.addEventListener('keydown', this._boundKeydown);

    // Close dropdown on outside click
    document.addEventListener('click', this._boundOutsideClick);

    // Browser back/forward: re-fetch grid for previous filter state
    window.addEventListener('popstate', this._boundPopstate);

    // Theme editor section reload compatibility
    this.addEventListener('shopify:section:load', () => this.reinit());
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._boundKeydown);
    document.removeEventListener('click', this._boundOutsideClick);
    window.removeEventListener('popstate', this._boundPopstate);

    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
    }
  }

  /**
   * Reinitialize after theme editor section reload.
   */
  reinit() {
    this.closeAllDropdowns();
    this.closeFilterDrawer();
    this.closeSortDrawer();
  }

  /* ============================================================
     EVENT DELEGATION
     ============================================================ */

  /**
   * Handle filter checkbox changes (desktop and mobile).
   * Desktop: apply immediately via AJAX.
   * Mobile: update live result count (actual apply happens on "Show results" click).
   */
  onFilterChange(event) {
    const input = event.target.closest('input[type="checkbox"]');
    if (!input) return;

    // Mobile drawer filter: update count only, don't apply yet
    if (input.hasAttribute('data-mobile-filter')) {
      this.updateMobileResultCount();
      return;
    }

    // Desktop: apply immediately
    const url = input.checked
      ? input.dataset.urlAdd
      : input.dataset.urlRemove;

    if (url) {
      this.applyFilters(url);
    }
  }

  /**
   * Click delegation handler for all interactive elements.
   */
  onClickDelegate(event) {
    const target = event.target;

    // Desktop filter dropdown trigger
    const filterTrigger = target.closest('.filter-bar__trigger');
    if (filterTrigger && filterTrigger.closest('.filter-bar')) {
      event.preventDefault();
      this.onTriggerClick(filterTrigger);
      return;
    }

    // Active filter pill remove
    const pill = target.closest('.active-filters__pill');
    if (pill) {
      event.preventDefault();
      const url = pill.dataset.url;
      if (url) {
        this.applyFilters(url);
      }
      return;
    }

    // Clear all filters
    const clearAll = target.closest('[data-clear-all]');
    if (clearAll) {
      event.preventDefault();
      const url = clearAll.href || clearAll.dataset.url || this.getCollectionUrl();
      this.applyFilters(url);
      return;
    }

    // Empty state "Clear all filters" button
    const clearFilters = target.closest('[data-clear-filters]');
    if (clearFilters) {
      event.preventDefault();
      this.applyFilters(this.getResetUrl());
      return;
    }

    // Desktop sort option
    const sortOption = target.closest('.sort-option:not([data-mobile-sort])');
    if (sortOption && sortOption.dataset.sortUrl) {
      event.preventDefault();
      this.applyFilters(sortOption.dataset.sortUrl);
      this.closeAllDropdowns();
      return;
    }

    // Mobile sort option (immediate apply + close drawer)
    const mobileSortOption = target.closest('[data-mobile-sort]');
    if (mobileSortOption && mobileSortOption.dataset.sortUrl) {
      event.preventDefault();
      this.applyFilters(mobileSortOption.dataset.sortUrl);
      this.closeSortDrawer();
      return;
    }

    // Price apply button
    const priceApply = target.closest('[data-price-apply]');
    if (priceApply) {
      event.preventDefault();
      this.onPriceApply(priceApply);
      return;
    }

    // Mobile drawer: open filter drawer
    const openFilters = target.closest('[data-open-filters]');
    if (openFilters) {
      event.preventDefault();
      this._triggerElement = openFilters;
      this.openFilterDrawer();
      return;
    }

    // Mobile drawer: open sort drawer
    const openSort = target.closest('[data-open-sort]');
    if (openSort) {
      event.preventDefault();
      this._triggerElement = openSort;
      this.openSortDrawer();
      return;
    }

    // Mobile drawer: close filter drawer
    const closeFilters = target.closest('[data-close-filters]');
    if (closeFilters) {
      event.preventDefault();
      this.closeFilterDrawer();
      return;
    }

    // Mobile drawer: close sort drawer
    const closeSort = target.closest('[data-close-sort]');
    if (closeSort) {
      event.preventDefault();
      this.closeSortDrawer();
      return;
    }

    // Filter drawer overlay click-to-close
    const filterOverlay = target.closest('[data-filter-overlay]');
    if (filterOverlay) {
      this.closeFilterDrawer();
      return;
    }

    // Sort drawer overlay click-to-close
    const sortOverlay = target.closest('[data-sort-overlay]');
    if (sortOverlay) {
      this.closeSortDrawer();
      return;
    }

    // Mobile drawer: apply filters
    const applyFilters = target.closest('[data-apply-filters]');
    if (applyFilters) {
      event.preventDefault();
      this.onMobileApply();
      return;
    }

    // Mobile drawer: clear all filters
    const mobileClearAll = target.closest('[data-mobile-clear-all]');
    if (mobileClearAll) {
      event.preventDefault();
      this.applyFilters(this.getResetUrl());
      this.closeFilterDrawer();
      return;
    }
  }

  /* ============================================================
     PRICE RANGE FILTER
     ============================================================ */

  /**
   * Handle price range "Apply" button click.
   * Reads min/max values from the price filter group inputs and builds a URL.
   */
  onPriceApply(button) {
    const group = button.closest('.filter-group, .filter-drawer__group');
    if (!group) return;

    const minInput = group.querySelector('input[type="number"]:first-of-type');
    const maxInput = group.querySelector('input[type="number"]:last-of-type');

    if (!minInput || !maxInput) return;

    const url = new URL(window.location.href);
    const params = url.searchParams;

    // Clear existing price params
    const minName = minInput.name;
    const maxName = maxInput.name;

    if (minInput.value) {
      params.set(minName, minInput.value);
    } else {
      params.delete(minName);
    }

    if (maxInput.value) {
      params.set(maxName, maxInput.value);
    } else {
      params.delete(maxName);
    }

    // Reset to page 1 on filter change
    params.delete('page');

    this.applyFilters(url.toString());
  }

  /* ============================================================
     AJAX FILTER APPLICATION (CORE METHOD)
     ============================================================ */

  /**
   * Apply filters via AJAX Section Rendering API.
   * Appends section_id to the fetch URL, updates history,
   * replaces grid content, active filters, and product count.
   *
   * @param {string} url - The URL with filter/sort params (without section_id)
   * @param {Object} [options] - Options
   * @param {boolean} [options.replace] - Use replaceState instead of pushState
   */
  async applyFilters(url, { replace = false } = {}) {
    if (this._isLoading) return;
    this._isLoading = true;

    const grid = document.getElementById('ProductGrid');
    if (grid) {
      grid.classList.add('collection-grid--loading');
    }

    // Disable all filter inputs to prevent double-click
    const inputs = this.querySelectorAll('input:not([data-mobile-filter])');
    inputs.forEach(input => { input.disabled = true; });

    const sectionId = this.dataset.sectionId;
    const fetchUrl = url.includes('?')
      ? `${url}&section_id=${sectionId}`
      : `${url}?section_id=${sectionId}`;

    // Update browser URL without section_id
    if (replace) {
      history.replaceState({}, '', url);
    } else {
      history.pushState({}, '', url);
    }

    try {
      const response = await fetch(fetchUrl);
      if (!response.ok) {
        throw new Error(`Filter fetch failed: ${response.status}`);
      }
      const html = await response.text();
      this.renderResults(html);
    } catch (error) {
      console.warn('[CollectionFilters] applyFilters failed:', error);
    } finally {
      if (grid) {
        grid.classList.remove('collection-grid--loading');
      }

      // Re-enable inputs
      const allInputs = this.querySelectorAll('input');
      allInputs.forEach(input => { input.disabled = false; });

      this._isLoading = false;
    }
  }

  /**
   * Parse server-rendered HTML and replace specific DOM targets.
   * HTML comes from Shopify's server (Liquid-rendered) -- safe to inject.
   *
   * @param {string} html - Server-rendered section HTML string
   */
  renderResults(html) {
    if (!html) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Replace the whole results container, not just #ProductGrid.
    //
    // The "Load more" block and the pagination bar live inside #CollectionContent
    // but OUTSIDE #ProductGrid. Swapping only the grid left them describing the
    // pre-filter result set: the stale button still pointed at page 2 of the
    // UNFILTERED collection (clicking it appended out-of-filter products), and a
    // filtered set larger than one page never got a button at all, stranding
    // everything past the first page.
    const newContent = doc.getElementById('CollectionContent');
    const currentContent = document.getElementById('CollectionContent');
    if (newContent && currentContent) {
      currentContent.innerHTML = newContent.innerHTML;
    } else {
      // Fallback for any template that doesn't wrap its grid.
      const newGrid = doc.getElementById('ProductGrid');
      const currentGrid = document.getElementById('ProductGrid');
      if (newGrid && currentGrid) {
        currentGrid.innerHTML = newGrid.innerHTML;
        currentGrid.className = newGrid.className;
      }
    }

    // Replace active filter pills
    const newPills = doc.getElementById('ActiveFilters');
    const currentPills = document.getElementById('ActiveFilters');
    if (newPills && currentPills) {
      currentPills.innerHTML = newPills.innerHTML;
    }

    // Replace product count (aria-live will announce)
    const newCount = doc.getElementById('ProductCount');
    const currentCount = document.getElementById('ProductCount');
    if (newCount && currentCount) {
      currentCount.innerHTML = newCount.innerHTML;
    }

    // Update desktop filter groups (counts and active states)
    this.updateDesktopFilters(doc);

    // Update mobile drawer content (result count in footer button)
    this.updateMobileDrawerContent(doc);

    // Dispatch event for other components
    document.dispatchEvent(new CustomEvent('filters:update', { bubbles: false }));

    // Scroll to top of grid if user has scrolled below it
    this.scrollToGridIfNeeded();
  }

  /**
   * Update desktop filter dropdown contents (counts, checked states).
   * @param {Document} doc - Parsed HTML document from server response
   */
  updateDesktopFilters(doc) {
    const filterBar = this.querySelector('.filter-bar');
    if (!filterBar) return;

    const newFilterBar = doc.querySelector('.filter-bar');
    if (!newFilterBar) return;

    // Replace filter groups content
    const groups = filterBar.querySelectorAll('.filter-group');
    groups.forEach(group => {
      const groupName = group.dataset.filterGroup;
      const newGroup = newFilterBar.querySelector(`[data-filter-group="${groupName}"]`);
      if (newGroup) {
        // Update trigger active state
        const trigger = group.querySelector('.filter-bar__trigger');
        const newTrigger = newGroup.querySelector('.filter-bar__trigger');
        if (trigger && newTrigger) {
          trigger.className = newTrigger.className;
        }

        // Update dropdown content (counts and checked states)
        const dropdown = group.querySelector('.filter-dropdown');
        const newDropdown = newGroup.querySelector('.filter-dropdown');
        if (dropdown && newDropdown) {
          dropdown.innerHTML = newDropdown.innerHTML;
        }
      }
    });

    // Update sort dropdown current label
    const sortTrigger = filterBar.querySelector('.filter-bar__sort .filter-bar__trigger');
    const newSortTrigger = newFilterBar.querySelector('.filter-bar__sort .filter-bar__trigger');
    if (sortTrigger && newSortTrigger) {
      sortTrigger.innerHTML = newSortTrigger.innerHTML;
    }

    // Update sort options active state
    const sortDropdown = filterBar.querySelector('.sort-dropdown');
    const newSortDropdown = newFilterBar.querySelector('.sort-dropdown');
    if (sortDropdown && newSortDropdown) {
      sortDropdown.innerHTML = newSortDropdown.innerHTML;
    }
  }

  /**
   * Update mobile drawer content after filter application.
   * @param {Document} doc - Parsed HTML document from server response
   */
  updateMobileDrawerContent(doc) {
    // Update filter drawer body
    const drawer = this.querySelector('[data-filter-drawer]');
    if (drawer) {
      const newDrawer = doc.querySelector('[data-filter-drawer]');
      if (newDrawer) {
        const body = drawer.querySelector('.filter-drawer__body');
        const newBody = newDrawer.querySelector('.filter-drawer__body');
        if (body && newBody) {
          body.innerHTML = newBody.innerHTML;
        }
      }
    }

    // Update the result count button text
    const applyBtn = this.querySelector('#FilterDrawerApply');
    if (applyBtn) {
      const newApplyBtn = doc.getElementById('FilterDrawerApply');
      if (newApplyBtn) {
        applyBtn.textContent = newApplyBtn.textContent;
      }
    }

    // Update sort drawer options
    const sortDrawer = this.querySelector('[data-sort-drawer]');
    if (sortDrawer) {
      const newSortDrawer = doc.querySelector('[data-sort-drawer]');
      if (newSortDrawer) {
        const body = sortDrawer.querySelector('.filter-drawer__body');
        const newBody = newSortDrawer.querySelector('.filter-drawer__body');
        if (body && newBody) {
          body.innerHTML = newBody.innerHTML;
        }
      }
    }
  }

  /**
   * Scroll to top of product grid if the user has scrolled below it.
   */
  scrollToGridIfNeeded() {
    const grid = document.getElementById('ProductGrid');
    if (!grid) return;

    const rect = grid.getBoundingClientRect();
    if (rect.top < 0) {
      grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /* ============================================================
     DESKTOP DROPDOWN MANAGEMENT
     ============================================================ */

  /**
   * Handle filter/sort trigger button clicks.
   * Toggles the associated dropdown, closes any other open dropdown.
   */
  onTriggerClick(trigger) {
    const controlsId = trigger.getAttribute('aria-controls');
    const dropdown = controlsId ? document.getElementById(controlsId) : null;
    if (!dropdown) return;

    const isCurrentlyOpen = !dropdown.hidden;

    // Close all open dropdowns first
    this.closeAllDropdowns();

    // If clicking the same trigger, just close (already handled above)
    if (isCurrentlyOpen) return;

    // Open this dropdown
    dropdown.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    this._activeDropdown = { trigger, dropdown };
  }

  /**
   * Close all desktop filter/sort dropdowns.
   */
  closeAllDropdowns() {
    // Close all filter dropdowns
    const dropdowns = this.querySelectorAll('.filter-dropdown, .sort-dropdown');
    dropdowns.forEach(d => { d.hidden = true; });

    // Reset all trigger aria-expanded
    const triggers = this.querySelectorAll('.filter-bar__trigger');
    triggers.forEach(t => { t.setAttribute('aria-expanded', 'false'); });

    this._activeDropdown = null;
  }

  /**
   * Handle clicks outside open dropdowns (document-level).
   */
  handleOutsideClick(event) {
    if (!this._activeDropdown) return;

    const { trigger, dropdown } = this._activeDropdown;

    // If the click is inside the dropdown or trigger, ignore
    if (trigger.contains(event.target) || dropdown.contains(event.target)) return;

    this.closeAllDropdowns();
  }

  /* ============================================================
     MOBILE FILTER DRAWER
     ============================================================ */

  /**
   * Open the mobile filter drawer (bottom sheet).
   * Sets aria-hidden, shows overlay, prevents background scroll, applies inert,
   * activates focus trap, and focuses close button.
   */
  openFilterDrawer() {
    const drawer = this.querySelector('[data-filter-drawer]');
    const overlay = this.querySelector('[data-filter-overlay]');
    if (!drawer) return;

    drawer.setAttribute('aria-hidden', 'false');

    if (overlay) {
      overlay.setAttribute('aria-hidden', 'false');
      overlay.classList.add('is-visible');
    }

    window.Theme.lockScroll();

    document.querySelector('#MainContent')?.setAttribute('inert', '');
    document.querySelector('#header-group')?.setAttribute('inert', '');

    this._releaseFocusTrap = this.trapFocus(drawer);

    const closeBtn = drawer.querySelector('[data-close-filters]');
    if (closeBtn) closeBtn.focus();

    this._mobileDrawerOpen = true;
  }

  /**
   * Close the mobile filter drawer.
   * Reverses all open state: aria-hidden, overlay, scroll, inert, focus trap.
   */
  closeFilterDrawer() {
    const drawer = this.querySelector('[data-filter-drawer]');
    const overlay = this.querySelector('[data-filter-overlay]');
    if (!drawer) return;

    drawer.setAttribute('aria-hidden', 'true');

    if (overlay) {
      overlay.setAttribute('aria-hidden', 'true');
      overlay.classList.remove('is-visible');
    }

    window.Theme.unlockScroll();

    document.querySelector('#MainContent')?.removeAttribute('inert');
    document.querySelector('#header-group')?.removeAttribute('inert');

    if (this._releaseFocusTrap) {
      this._releaseFocusTrap();
      this._releaseFocusTrap = null;
    }

    if (this._triggerElement) {
      this._triggerElement.focus();
      this._triggerElement = null;
    }

    this._mobileDrawerOpen = false;
  }

  /**
   * Open the mobile sort drawer (bottom sheet).
   */
  openSortDrawer() {
    const drawer = this.querySelector('[data-sort-drawer]');
    const overlay = this.querySelector('[data-sort-overlay]');
    if (!drawer) return;

    drawer.setAttribute('aria-hidden', 'false');

    if (overlay) {
      overlay.setAttribute('aria-hidden', 'false');
      overlay.classList.add('is-visible');
    }

    window.Theme.lockScroll();

    document.querySelector('#MainContent')?.setAttribute('inert', '');
    document.querySelector('#header-group')?.setAttribute('inert', '');

    this._releaseSortFocusTrap = this.trapFocus(drawer);

    const closeBtn = drawer.querySelector('[data-close-sort]');
    if (closeBtn) closeBtn.focus();

    this._sortDrawerOpen = true;
  }

  /**
   * Close the mobile sort drawer.
   */
  closeSortDrawer() {
    const drawer = this.querySelector('[data-sort-drawer]');
    const overlay = this.querySelector('[data-sort-overlay]');
    if (!drawer) return;

    drawer.setAttribute('aria-hidden', 'true');

    if (overlay) {
      overlay.setAttribute('aria-hidden', 'true');
      overlay.classList.remove('is-visible');
    }

    window.Theme.unlockScroll();

    document.querySelector('#MainContent')?.removeAttribute('inert');
    document.querySelector('#header-group')?.removeAttribute('inert');

    if (this._releaseSortFocusTrap) {
      this._releaseSortFocusTrap();
      this._releaseSortFocusTrap = null;
    }

    if (this._triggerElement) {
      this._triggerElement.focus();
      this._triggerElement = null;
    }

    this._sortDrawerOpen = false;
  }

  /**
   * Handle mobile filter drawer "Show N results" button click.
   * Builds a URL from all checked mobile filter inputs and applies.
   */
  onMobileApply() {
    const drawer = this.querySelector('[data-filter-drawer]');
    if (!drawer) return;

    // Collect checked filter inputs from the mobile drawer
    const checkedInputs = drawer.querySelectorAll('input[type="checkbox"][data-mobile-filter]:checked');
    const priceInputs = drawer.querySelectorAll('input[type="number"][data-mobile-filter]');

    // Start with the base collection URL
    let url;

    // Preserve current sort_by param if one is active
    const currentUrl = new URL(window.location.href);
    const currentSort = currentUrl.searchParams.get('sort_by');

    if (checkedInputs.length === 0 && Array.from(priceInputs).every(i => !i.value)) {
      const baseUrl = new URL(this.getCollectionUrl(), window.location.origin);
      if (currentSort) {
        baseUrl.searchParams.set('sort_by', currentSort);
      }
      url = baseUrl.toString();
    } else {
      const baseUrl = new URL(this.getCollectionUrl(), window.location.origin);
      const params = baseUrl.searchParams;

      // Preserve sort
      if (currentSort) {
        params.set('sort_by', currentSort);
      }

      // Add checkbox filters
      checkedInputs.forEach(input => {
        params.append(input.name, input.value);
      });

      // Add price range filters
      priceInputs.forEach(input => {
        if (input.value) {
          params.set(input.name, input.value);
        }
      });

      url = baseUrl.toString();
    }

    this.applyFilters(url);
    this.closeFilterDrawer();
  }

  /**
   * Update the mobile drawer "Show N results" button text with live count.
   * Debounced at 300ms to prevent request flooding.
   */
  updateMobileResultCount() {
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
    }

    this._debounceTimer = setTimeout(async () => {
      const drawer = this.querySelector('[data-filter-drawer]');
      if (!drawer) return;

      // Build URL from current mobile drawer state
      const checkedInputs = drawer.querySelectorAll('input[type="checkbox"][data-mobile-filter]:checked');
      const priceInputs = drawer.querySelectorAll('input[type="number"][data-mobile-filter]');

      const baseUrl = new URL(this.getCollectionUrl(), window.location.origin);
      const params = baseUrl.searchParams;

      checkedInputs.forEach(input => {
        params.append(input.name, input.value);
      });

      priceInputs.forEach(input => {
        if (input.value) {
          params.set(input.name, input.value);
        }
      });

      const sectionId = this.dataset.sectionId;
      params.set('section_id', sectionId);

      try {
        const response = await fetch(baseUrl.toString());
        if (!response.ok) return;

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        // Extract product count from the response
        const countEl = doc.getElementById('ProductCount');
        if (countEl) {
          const applyBtn = this.querySelector('#FilterDrawerApply');
          if (applyBtn) {
            // Extract the count text and update the button
            const newApplyBtn = doc.getElementById('FilterDrawerApply');
            if (newApplyBtn) {
              applyBtn.textContent = newApplyBtn.textContent;
            }
          }
        }
      } catch (error) {
        // Non-fatal: button keeps showing previous count
        console.warn('[CollectionFilters] updateMobileResultCount failed:', error);
      }
    }, 300);
  }

  /* ============================================================
     FOCUS TRAP
     ============================================================ */

  /**
   * Focus trap implementation for mobile drawers.
   * Re-queries focusable elements on EVERY Tab keypress to handle dynamic content.
   * Exact pattern from cart-drawer.js.
   *
   * @param {HTMLElement} container - The container to trap focus within
   * @returns {Function} Cleanup function to release the trap
   */
  trapFocus(container) {
    const focusableSelectors = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

    // Re-query on every Tab press (not cached at open time)
    const getFocusable = () => Array.from(container.querySelectorAll(focusableSelectors));

    const handleKeydown = (e) => {
      if (e.key !== 'Tab') return;

      const focusable = getFocusable();
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        // Shift+Tab: wrap backward from first to last
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // Tab: wrap forward from last to first
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    container.addEventListener('keydown', handleKeydown);

    return () => {
      container.removeEventListener('keydown', handleKeydown);
    };
  }

  /* ============================================================
     KEYBOARD AND POPSTATE HANDLERS
     ============================================================ */

  /**
   * Handle global keydown events.
   * Escape closes active dropdown or mobile drawer.
   */
  handleKeydown(e) {
    if (e.key !== 'Escape') return;

    if (this._mobileDrawerOpen) {
      this.closeFilterDrawer();
      return;
    }

    if (this._sortDrawerOpen) {
      this.closeSortDrawer();
      return;
    }

    if (this._activeDropdown) {
      this.closeAllDropdowns();
    }
  }

  /**
   * Handle browser back/forward navigation.
   * Re-fetches and re-renders the grid for the URL the user navigated to.
   * Uses replaceState to avoid pushing a duplicate entry on back/forward.
   */
  handlePopstate() {
    this.applyFilters(window.location.href, { replace: true });
  }

  /* ============================================================
     UTILITY
     ============================================================ */

  /**
   * The base URL identifying the current result set, with no filter params.
   *
   * On /search the `q` term IS the result set — it is not a filter. Returning a
   * bare pathname here dropped it from every URL this component builds (mobile
   * apply, clear-all, the result-count preflight), turning each of those into a
   * query-less /search that renders zero results.
   *
   * @returns {string} Clean collection URL, preserving ?q= on search
   */
  getCollectionUrl() {
    const base = new URL(window.location.pathname, window.location.origin);
    const term = new URL(window.location.href).searchParams.get('q');
    if (term) base.searchParams.set('q', term);
    return base.pathname + base.search;
  }

  /**
   * Base URL plus the active sort. Used by every "clear filters" path — clearing
   * filters should not silently also reset the shopper's chosen sort order.
   *
   * @returns {string} Reset URL preserving ?q= and ?sort_by=
   */
  getResetUrl() {
    const reset = new URL(this.getCollectionUrl(), window.location.origin);
    const sort = new URL(window.location.href).searchParams.get('sort_by');
    if (sort) reset.searchParams.set('sort_by', sort);
    return reset.pathname + reset.search;
  }
}

// Registration guard: only define once even if section is rendered multiple times
if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', CollectionFilters);
}
